import React from "react"
import moment from "moment"
import { notification } from "antd"
import api from "src/api"

export const getDefaultFilters = () => ({
  dateFrom: moment().subtract(6, "days").format("YYYY-MM-DD"),
  dateTo: moment().format("YYYY-MM-DD"),
  phone: "",
  method: null,
  status: null,
  direction: "desc",
})

// Параметры запроса: даты по Москве (ГГГГ-ММ-ДД), часть номера, этап, статус, сортировка.
export function buildQuery(filters, extra = {}) {
  const params = new URLSearchParams({ date_from: filters.dateFrom, date_to: filters.dateTo })
  if (filters.phone) params.append("phone", filters.phone)
  if (filters.method) params.append("method", filters.method)
  if (filters.status) params.append("status", filters.status)
  if (filters.direction) params.append("direction", filters.direction)
  Object.entries(extra).forEach(([key, value]) => params.append(key, String(value)))

  return params.toString()
}

function showError(message, error) {
  notification.error({ message, description: error?.response?.data?.message })
}

// Журнал авторизаций: список запросов, сводка за период и каскад одного процесса входа.
export function useAuthRequests() {
  const [filters, setFilters] = React.useState(getDefaultFilters)
  const [items, setItems] = React.useState([])
  const [pagination, setPagination] = React.useState({ current: 1, pageSize: 20, total: 0 })
  const [summary, setSummary] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSummaryLoading, setIsSummaryLoading] = React.useState(false)
  const [isExporting, setIsExporting] = React.useState(false)
  // Ответ на устаревший запрос (фильтр успели поменять) не показываем.
  const listRequest = React.useRef(0)
  const summaryRequest = React.useRef(0)

  React.useEffect(() => {
    load(filters, 1, pagination.pageSize)
  }, [])

  function loadList(nextFilters, page, pageSize) {
    const requestId = ++listRequest.current
    setIsLoading(true)
    api
      .get(`auth-requests?${buildQuery(nextFilters, { page, countOnPage: pageSize })}`, { useV2Api: true })
      .then(({ data }) => {
        if (requestId !== listRequest.current) return
        setItems(data.items ?? [])
        setPagination({
          current: data.pagination.currentPage,
          pageSize: data.pagination.countOnPage,
          total: data.pagination.total,
        })
      })
      .catch((error) => showError("Не удалось загрузить авторизации", error))
      .finally(() => {
        if (requestId === listRequest.current) setIsLoading(false)
      })
  }

  function loadSummary(nextFilters) {
    const requestId = ++summaryRequest.current
    setIsSummaryLoading(true)
    api
      .get(`auth-requests/summary?${buildQuery(nextFilters)}`, { useV2Api: true })
      .then(({ data }) => {
        if (requestId === summaryRequest.current) setSummary(data)
      })
      .catch((error) => showError("Не удалось загрузить сводку", error))
      .finally(() => {
        if (requestId === summaryRequest.current) setIsSummaryLoading(false)
      })
  }

  function load(nextFilters, page, pageSize) {
    loadList(nextFilters, page, pageSize)
    loadSummary(nextFilters)
  }

  // Период и номер меняют и сводку, способ и статус — только список.
  function applyFilters(changed) {
    const nextFilters = { ...filters, ...changed }
    setFilters(nextFilters)
    const summaryChanged = ["dateFrom", "dateTo", "phone"].some((key) => key in changed && changed[key] !== filters[key])
    loadList(nextFilters, 1, pagination.pageSize)
    if (summaryChanged) loadSummary(nextFilters)
  }

  function refresh() {
    load(filters, pagination.current, pagination.pageSize)
  }

  function handleTableChange(pag, _tableFilters, sorter) {
    const direction = sorter?.order === "ascend" ? "asc" : "desc"
    if (direction !== filters.direction) {
      const nextFilters = { ...filters, direction }
      setFilters(nextFilters)
      loadList(nextFilters, 1, pag.pageSize)
      return
    }
    loadList(filters, pag.current, pag.pageSize)
  }

  function loadProcess(processId) {
    return api.get(`auth-requests/processes/${processId}`, { useV2Api: true }).then(({ data }) => data.item)
  }

  async function exportCsv() {
    setIsExporting(true)
    try {
      const file = await api.get(`auth-requests/export?${buildQuery(filters)}`, { useV2Api: true, responseType: "blob" })
      const url = window.URL.createObjectURL(new Blob([file], { type: "text/csv;charset=utf-8" }))
      const link = document.createElement("a")
      link.href = url
      link.download = `auth-requests-${filters.dateFrom}-${filters.dateTo}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      showError("Ошибка выгрузки CSV", error)
    } finally {
      setIsExporting(false)
    }
  }

  return {
    filters,
    items,
    pagination,
    summary,
    isLoading,
    isSummaryLoading,
    isExporting,
    applyFilters,
    refresh,
    handleTableChange,
    loadProcess,
    exportCsv,
  }
}
